import { useEffect } from 'react';

import { getFocusableElements } from '@/utils/focusOrder/focusOrder';
import { hasOpenDialog } from '@/utils/hasOpenDialog/hasOpenDialog';

import type { TUseDocumentViewerKeyboardParams } from './types';

const CONVERSATION_SELECTOR = '.ApplicationLayoutBody';

/*
 * Moves focus to `target`, cancelling the browser's own tab keydown move.
 */
const focusInstead = (event: KeyboardEvent, target: HTMLElement | null) => {
  if (!target) return;
  event.preventDefault();
  target.focus();
};
/*
 * Gets the last focusable element that precedes `element` in the document order.
 */
const lastFocusableBefore = (element: HTMLElement): HTMLElement | null =>
  getFocusableElements(document)
    .filter((candidate) => (candidate.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0)
    .at(-1) ?? null;

/*
 * Keeps tab keydown navigation inside the viewer while it behaves as a modal dialog.
 */
const containFocus = (event: KeyboardEvent, container: HTMLElement) => {
  if (hasOpenDialog(container)) return;

  const items = getFocusableElements(container);
  if (items.length === 0) return;

  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;

  if (!container.contains(active)) {
    focusInstead(event, first);
    return;
  }
  if (event.shiftKey && active === first) focusInstead(event, last);
  if (!event.shiftKey && active === last) focusInstead(event, first);
};

/**
 * Makes tab keydown follow the reading order, first the conversation and then the viewer while the two sit side by side.
 *
 * The viewer precedes the conversation in the DOM because rio's application layout floats a right sidebar,
 * so the browser would otherwise tab through it first. Only the four boundary presses are redirected
 * and everything inside either group is left to the browser order.
 */
const followReadingOrder = (event: KeyboardEvent, container: HTMLElement) => {
  const conversation = document.querySelector<HTMLElement>(CONVERSATION_SELECTOR);
  const active = document.activeElement;
  if (!conversation || !active) return;

  const viewerItems = getFocusableElements(container);
  const conversationItems = getFocusableElements(conversation);
  if (viewerItems.length === 0 || conversationItems.length === 0) return;

  const isInViewer = container.contains(active);
  const isInConversation = conversation.contains(active);

  if (isInConversation && !event.shiftKey && active === conversationItems[conversationItems.length - 1]) {
    focusInstead(event, viewerItems[0]);
    return;
  }
  if (isInViewer && event.shiftKey && active === viewerItems[0]) {
    focusInstead(event, conversationItems[conversationItems.length - 1]);
    return;
  }
  if (isInConversation && event.shiftKey && active === conversationItems[0]) {
    focusInstead(event, lastFocusableBefore(container));
    return;
  }
  if (isInViewer && !event.shiftKey && active === viewerItems[viewerItems.length - 1]) {
    // INFO: The viewer is the end of the reading order, so the tab leaves the application instead of wrapping
    // back into the conversation.
    conversationItems[conversationItems.length - 1].focus();
  }
};

/**
 * Owns the Document viewer's keyboard behaviour: Esc to close, and where tab goes.
 *
 * Esc is scoped to the Viewer as it only closes while focus is inside it, never globally. In full
 * screen the press is left alone so rio's own handler leaves full screen first and the next Esc closes.
 */
export const useDocumentViewerKeyboard = ({
  containerRef,
  isModal,
  isFullScreen,
  onClose,
}: TUseDocumentViewerKeyboardParams) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const container = containerRef.current;
      if (!container) return;

      if (event.key === 'Escape') {
        if (isFullScreen || !container.contains(document.activeElement)) return;
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;

      if (isModal) containFocus(event, container);
      else followReadingOrder(event, container);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // INFO: Both flags change what a press does, so the listener is rebound when either does.
  }, [containerRef, isModal, isFullScreen, onClose]);
};
